import {
    useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LuSearch, LuArrowUp, LuArrowDown, LuCornerDownLeft, LuLoaderCircle } from 'react-icons/lu';

import {
    FONTES, ACOES, ICONE_TEMA, telasBuscaveis, corresponde, normalizar, semearBusca,
    type ResultadoBusca, type FonteBusca, type AcaoBusca, type TelaBuscavel,
} from '../config/buscaGlobal';
import { canAccessRoute, getUserPermissions } from '../config/permissions';
import { useTheme } from '../contexts/ThemeContext';
import './BuscaGlobal.css';

/**
 * A BUSCA GLOBAL — a paleta do Ctrl+K.
 *
 * Proposta de 2026-10-01 (`docs/propostas/busca-global-ctrl-k.html`), Direção A.
 * Substitui o campo que morava dentro da sidebar e que achava só oportunidade.
 *
 * **Por que ela saiu da sidebar.** Enquanto a busca era filha de `.sidebar` ela
 * herdava três defeitos que não eram dela: a largura de 320px, o recolhimento
 * (Ctrl+K reorganizava a tela por baixo para poder buscar) e, abaixo de 768px,
 * a própria ausência — no celular não havia busca nenhuma. Aqui ela é um portal
 * em `document.body`: a mesma paleta serve o computador e o telefone.
 *
 * **O que ela alcança.** Tudo o que está em `config/buscaGlobal.ts`: as 13
 * fontes de registro, as telas dos dois mapas de navegação e as ações. Este
 * componente não sabe o nome de nenhuma entidade — ele carrega, filtra, desenha
 * e navega. Entidade nova entra lá, não aqui.
 *
 * **Permissão.** Não há regra própria: todo resultado é uma navegação, então
 * `canAccessRoute` decide, e ela já cobre permissão e funcionalidade desligada.
 * Grupo que a pessoa não alcança **nem é carregado** — a busca não chega a ver
 * o dado da tela que ela não vê.
 */

/** Com menos letras a busca traz registro demais para ser atalho. */
const MINIMO = 2;
/** Quanto esperar a pessoa parar de digitar antes de perguntar ao servidor. */
const ESPERA_MS = 300;
/** Quantos por grupo. É atalho, não lista — o resto fica no "Ver todos". */
const POR_GRUPO = 5;
/** Telas mostradas com o campo vazio, antes de digitar. */
const TELAS_NO_VAZIO = 5;

/** Qualquer tela pede a paleta por aqui — evita contexto só para abrir um modal. */
export const ABRIR_BUSCA_EVENT = 'lemeia:abrir-busca-global';
export const abrirBuscaGlobal = () => window.dispatchEvent(new Event(ABRIR_BUSCA_EVENT));

/* ─── O QUE O TECLADO PERCORRE ────────────────────────────────── */

/** Uma linha escolhível. O teclado anda sobre esta lista plana, não sobre os grupos. */
interface Escolhivel {
    chave: string;
    /** Só para ordenar a escolha inicial — ver `melhorLinha`. */
    titulo: string;
    escolher: () => void;
}

interface OpProps {
    /** Posição na lista plana — é o que o teclado compara. */
    i: number;
    ativo: number;
    aoMover: (i: number) => void;
    icone?: ReactNode;
    titulo: ReactNode;
    subtitulo?: string;
    etiqueta?: string;
    tom?: ResultadoBusca['tom'];
    aoEscolher: () => void;
}

/** Uma linha desenhada. Exatamente uma fica realçada — a escolhida. */
const Op: FC<OpProps> = ({ i, ativo, aoMover, icone, titulo, subtitulo, etiqueta, tom, aoEscolher }) => (
    <button
        type="button"
        className="pal-op"
        data-ativo={i === ativo}
        /* Mexer o mouse MOVE a seleção, não acende uma segunda. Há sempre
           exatamente uma linha realçada, venha ela do teclado ou da mão —
           por isso não existe estado de hover nesta lista. */
        onMouseMove={() => { if (i !== ativo) aoMover(i); }}
        onClick={aoEscolher}
    >
        {icone && <span className="pal-ic">{icone}</span>}
        <span className="pal-tit">{titulo}</span>
        {subtitulo && <span className="pal-sub">{subtitulo}</span>}
        {etiqueta && <span className={`pal-pill is-${tom ?? 'neutro'}`}>{etiqueta}</span>}
    </button>
);

/** O estado de carregamento de uma fonte. */
type Estado = 'parada' | 'carregando' | 'pronta' | 'erro';

interface Props {
    onLogout: () => void;
}

const BuscaGlobal: FC<Props> = ({ onLogout }) => {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    const [aberta, setAberta] = useState(false);
    /** O que está no campo, letra a letra. */
    const [texto, setTexto] = useState('');
    /** O que vai ao servidor: o texto, depois da espera. */
    const [buscaServidor, setBuscaServidor] = useState('');
    const [ativo, setAtivo] = useState(0);

    /** O que cada fonte devolveu, carregada no máximo uma vez por sessão. */
    const [cache, setCache] = useState<Record<string, ResultadoBusca[]>>({});
    const [estados, setEstados] = useState<Record<string, Estado>>({});
    /** Fonte já pedida — o pedido não pode sair duas vezes enquanto o primeiro corre. */
    const pedidas = useRef<Set<string>>(new Set());

    const campoRef = useRef<HTMLInputElement>(null);
    const listaRef = useRef<HTMLDivElement>(null);
    const espera = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const permissoes = useMemo(() => (aberta ? getUserPermissions() : []), [aberta]);

    /* ─── O QUE ESTA PESSOA ALCANÇA ───────────────────────────────
       Uma regra só para entidade, tela e ação: a rota de destino. */
    const fontesVisiveis = useMemo(
        () => (aberta ? FONTES.filter(f => canAccessRoute(f.rota, permissoes)) : []),
        [aberta, permissoes],
    );
    const telas = useMemo(
        () => (aberta ? telasBuscaveis().filter(t => canAccessRoute(t.to, permissoes)) : []),
        [aberta, permissoes],
    );
    const acoes = useMemo(
        () => (aberta ? ACOES.filter(a => canAccessRoute(a.rota, permissoes)) : []),
        [aberta, permissoes],
    );

    /* ─── ABRIR E FECHAR ──────────────────────────────────────── */

    const fechar = useCallback(() => {
        setAberta(false);
        clearTimeout(espera.current);
        setTexto('');
        setBuscaServidor('');
        setAtivo(0);
    }, []);

    useEffect(() => {
        const aoTeclar = (evento: KeyboardEvent) => {
            if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
                /* Enquanto um menu de teclado estiver aberto (a barra do editor
                   de anotações), o atalho é dele. */
                if (document.querySelector('[data-atalho-busca="off"]')) return;
                evento.preventDefault();
                setAberta(atual => !atual);
            }
        };
        const aoPedirem = () => setAberta(true);
        window.addEventListener('keydown', aoTeclar);
        window.addEventListener(ABRIR_BUSCA_EVENT, aoPedirem);
        return () => {
            window.removeEventListener('keydown', aoTeclar);
            window.removeEventListener(ABRIR_BUSCA_EVENT, aoPedirem);
        };
    }, []);

    /* Abriu: foca o campo e tranca a rolagem de trás — mesma conduta da gaveta
       do celular, senão a página corre atrás do véu. */
    useEffect(() => {
        if (!aberta) return;
        const antes = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const t = requestAnimationFrame(() => campoRef.current?.focus());
        return () => {
            document.body.style.overflow = antes;
            cancelAnimationFrame(t);
        };
    }, [aberta]);

    /* ─── CARREGAR ────────────────────────────────────────────────
       Só quando há texto que vale a pena, e uma vez por fonte por sessão. As
       fontes partem JUNTAS e cada uma preenche o seu grupo quando chega: a
       primeira resposta aparece sem esperar a última. */
    const procurado = texto.trim();
    const vale = procurado.length >= MINIMO;

    useEffect(() => {
        if (!aberta || !vale) return;
        fontesVisiveis.forEach(fonte => {
            if (fonte.servidor || pedidas.current.has(fonte.id)) return;
            pedidas.current.add(fonte.id);
            setEstados(e => ({ ...e, [fonte.id]: 'carregando' }));
            fonte.carregar('')
                .then(itens => {
                    setCache(c => ({ ...c, [fonte.id]: itens }));
                    setEstados(e => ({ ...e, [fonte.id]: 'pronta' }));
                })
                .catch(() => {
                    setEstados(e => ({ ...e, [fonte.id]: 'erro' }));
                    /* Tira da lista de pedidas: a próxima busca tenta de novo.
                       Rede que caiu uma vez não condena a sessão inteira. */
                    pedidas.current.delete(fonte.id);
                });
        });
    }, [aberta, vale, fontesVisiveis]);

    /* A espera só existe para quem pergunta ao servidor a cada letra. */
    useEffect(() => {
        clearTimeout(espera.current);
        if (!vale) { setBuscaServidor(''); return; }
        espera.current = setTimeout(() => setBuscaServidor(procurado), ESPERA_MS);
        return () => clearTimeout(espera.current);
    }, [procurado, vale]);

    useEffect(() => {
        if (!aberta || !buscaServidor) return;
        fontesVisiveis.filter(f => f.servidor).forEach(fonte => {
            setEstados(e => ({ ...e, [fonte.id]: 'carregando' }));
            fonte.carregar(buscaServidor)
                .then(itens => {
                    setCache(c => ({ ...c, [fonte.id]: itens }));
                    setEstados(e => ({ ...e, [fonte.id]: 'pronta' }));
                })
                .catch(() => setEstados(e => ({ ...e, [fonte.id]: 'erro' })));
        });
    }, [aberta, buscaServidor, fontesVisiveis]);

    /* ─── IR ──────────────────────────────────────────────────── */

    const ir = useCallback((to: string, estado?: Record<string, unknown>) => {
        fechar();
        navigate(to, estado ? { state: estado } : undefined);
    }, [fechar, navigate]);

    /* O rótulo que a pessoa LÊ. A ação de tema muda de nome com o tema atual, e
       enquanto o ranking olhava o rótulo do registro ("Trocar o tema") e a tela
       desenhava outro ("Tema escuro"), digitar "tema" não escolhia a ação. */
    const rotuloDaAcao = useCallback((a: AcaoBusca) => (
        a.efeito === 'tema' ? (theme === 'dark' ? 'Tema claro' : 'Tema escuro') : a.titulo
    ), [theme]);

    const executar = useCallback((acao: AcaoBusca) => {
        if (acao.efeito === 'tema') { fechar(); toggleTheme(); return; }
        if (acao.efeito === 'sair') { fechar(); onLogout(); return; }
        /* `novo: true` é o combinado com as telas de cadastro: quem souber ler
           abre o formulário já aberto; quem não souber ignora e mostra a lista,
           que é o pior caso aceitável. */
        if (acao.to) ir(acao.to, { novo: true });
    }, [fechar, ir, onLogout, toggleTheme]);

    /* ─── O QUE APARECE ───────────────────────────────────────── */

    interface Grupo {
        id: string;
        rotulo: string;
        icone: FonteBusca['icone'];
        itens: ResultadoBusca[];
        total: number;
        estado: Estado;
        verTodos?: FonteBusca['verTodos'];
    }

    const grupos: Grupo[] = useMemo(() => {
        if (!vale) return [];
        return fontesVisiveis.map(fonte => {
            const todos = cache[fonte.id] ?? [];
            /* A fonte que busca no servidor já devolve filtrado; filtrar de novo
               aqui descartaria o que ela achou no conteúdo do artigo. */
            const achados = fonte.servidor ? todos : todos.filter(r => corresponde(procurado, r.termos));
            return {
                id: fonte.id,
                rotulo: fonte.grupo,
                icone: fonte.icone,
                itens: achados.slice(0, POR_GRUPO),
                total: achados.length,
                estado: estados[fonte.id] ?? 'parada',
                verTodos: fonte.verTodos,
            };
        }).filter(g => g.itens.length > 0 || g.estado === 'carregando' || g.estado === 'erro');
    }, [vale, fontesVisiveis, cache, estados, procurado]);

    const telasAchadas = useMemo(() => {
        if (!vale) return telas.slice(0, TELAS_NO_VAZIO);
        return telas.filter(t => corresponde(procurado, t.termos));
    }, [vale, telas, procurado]);

    const acoesAchadas = useMemo(() => {
        if (!vale) return acoes;
        return acoes.filter(a => corresponde(procurado, [a.titulo, a.descricao, ...a.termos]));
    }, [vale, acoes, procurado]);

    /* A lista plana que o teclado percorre, na MESMA ordem do desenho. */
    const linhas: Escolhivel[] = useMemo(() => {
        const fila: Escolhivel[] = [];
        grupos.forEach(g => {
            g.itens.forEach(r => fila.push({
                chave: r.chave,
                titulo: r.titulo,
                escolher: () => {
                    if (g.verTodos && r.to === g.verTodos.to) semearBusca(g.verTodos.chave, procurado, g.verTodos.campo);
                    ir(r.to);
                },
            }));
            if (g.verTodos && g.total > g.itens.length) {
                const destino = g.verTodos;
                fila.push({
                    chave: `${g.id}-todos`,
                    titulo: '',
                    escolher: () => { semearBusca(destino.chave, procurado, destino.campo); ir(destino.to); },
                });
            }
        });
        telasAchadas.forEach(t => fila.push({ chave: t.chave, titulo: t.titulo, escolher: () => ir(t.to) }));
        acoesAchadas.forEach(a => fila.push({ chave: a.chave, titulo: rotuloDaAcao(a), escolher: () => executar(a) }));
        return fila;
    }, [grupos, telasAchadas, acoesAchadas, procurado, ir, executar, rotuloDaAcao]);

    const linhasRef = useRef(linhas);
    linhasRef.current = linhas;
    const ativoRef = useRef(ativo);
    ativoRef.current = ativo;

    /* A escolha inicial é a MELHOR correspondência, não a primeira do primeiro
       grupo. A ordem dos grupos é deliberada (quem digita um nome quer o
       negócio), mas ela sozinha fazia "novo contato" cair num artigo de ajuda
       chamado "Como cadastrar um contato", porque Ajuda vem antes de Ações.
       Título que COMEÇA com o que foi digitado ganha de título que apenas
       contém — é o que faz Enter abrir o que a pessoa escreveu. */
    const melhorLinha = useMemo(() => {
        if (!vale) return 0;
        const alvo = normalizar(procurado);
        const exata = linhas.findIndex(l => normalizar(l.titulo) === alvo);
        if (exata >= 0) return exata;
        const comeca = linhas.findIndex(l => l.titulo && normalizar(l.titulo).startsWith(alvo));
        return comeca >= 0 ? comeca : 0;
    }, [vale, procurado, linhas]);

    /* Só reposiciona quando o TEXTO muda ou quando a melhor aposta muda porque
       uma fonte terminou de chegar — nunca depois de a pessoa ter andado com as
       setas, senão a seleção pularia debaixo da mão dela. */
    const mexeu = useRef(false);
    useEffect(() => { mexeu.current = false; }, [procurado]);
    useEffect(() => {
        if (!mexeu.current) setAtivo(melhorLinha);
    }, [melhorLinha]);

    /* A lista encolheu por baixo do cursor (uma fonte terminou e filtrou): o
       índice não pode ficar apontando para o vazio. */
    useEffect(() => {
        setAtivo(a => (a >= linhas.length ? Math.max(0, linhas.length - 1) : a));
    }, [linhas.length]);

    const aindaCarregando = grupos.some(g => g.estado === 'carregando');

    /* ─── TECLADO ─────────────────────────────────────────────── */

    const aoTeclarNoCampo = (evento: React.KeyboardEvent<HTMLInputElement>) => {
        const total = linhasRef.current.length;
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(evento.key)) mexeu.current = true;
        switch (evento.key) {
            case 'ArrowDown':
                evento.preventDefault();
                if (total) setAtivo(a => (a + 1) % total);
                break;
            case 'ArrowUp':
                evento.preventDefault();
                if (total) setAtivo(a => (a - 1 + total) % total);
                break;
            case 'Home':
                evento.preventDefault();
                setAtivo(0);
                break;
            case 'End':
                evento.preventDefault();
                setAtivo(Math.max(0, total - 1));
                break;
            case 'Enter': {
                evento.preventDefault();
                const linha = linhasRef.current[ativoRef.current];
                if (linha) linha.escolher();
                break;
            }
            case 'Escape':
                evento.preventDefault();
                fechar();
                break;
            default:
                break;
        }
    };

    /* A linha escolhida tem que estar à vista — andar com ↓ não pode sair da
       janela rolável sem levar a rolagem junto. */
    useEffect(() => {
        if (!aberta) return;
        const alvo = listaRef.current?.querySelector<HTMLElement>('[data-ativo="true"]');
        alvo?.scrollIntoView({ block: 'nearest' });
    }, [ativo, aberta]);

    if (!aberta) return null;

    /* ─── DESENHO ─────────────────────────────────────────────── */

    let indice = -1;
    const proximo = () => { indice += 1; return indice; };

    /* Uma linha da paleta. `Op` mora fora do componente de propósito: definida
       aqui dentro, ela seria um tipo novo a cada tecla e o React remontaria a
       lista inteira em vez de atualizá-la. */
    /* No React 19 o `key` dentro de um espalhamento não é mais tratado como
       especial, então ele sai do objeto e vai explícito no elemento. */
    const linha = ({ key, ...props }: Omit<OpProps, 'ativo' | 'aoMover'> & { key: string }) => (
        <Op key={key} {...props} ativo={ativo} aoMover={setAtivo} />
    );

    const nada = grupos.length === 0 && telasAchadas.length === 0 && acoesAchadas.length === 0;

    return createPortal(
        <div className="pal-overlay" role="presentation" onMouseDown={fechar}>
            <div
                className="pal-caixa"
                role="dialog"
                aria-modal="true"
                aria-label="Buscar na plataforma"
                onMouseDown={e => e.stopPropagation()}
            >
                <div className="pal-campo">
                    <LuSearch className="pal-campo-ic" aria-hidden="true" />
                    <input
                        ref={campoRef}
                        type="text"
                        value={texto}
                        onChange={e => setTexto(e.target.value)}
                        onKeyDown={aoTeclarNoCampo}
                        placeholder="Buscar registros, telas e ações…"
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="O que você procura"
                    />
                    {aindaCarregando && <LuLoaderCircle className="pal-girando" aria-label="Buscando" />}
                    {/* Só aparece no celular (CSS): lá não há Esc, e a caixa ocupa a tela
                        inteira, sem fundo para tocar fora. */}
                    <button type="button" className="pal-fechar" onClick={fechar}>Cancelar</button>
                </div>

                <div className="pal-lista" ref={listaRef}>
                    {nada && (
                        <p className="pal-vazio">
                            Nada encontrado para <b>“{procurado}”</b>.
                        </p>
                    )}

                    {grupos.map(g => {
                        const Icone = g.icone;
                        return (
                            <div className="pal-grupo" key={g.id}>
                                <div className="pal-grupo-tit">{g.rotulo}</div>
                                {g.estado === 'erro' && g.itens.length === 0 && (
                                    <p className="pal-recado">Não deu para buscar em {g.rotulo.toLowerCase()} agora.</p>
                                )}
                                {g.estado === 'carregando' && g.itens.length === 0 && (
                                    <p className="pal-recado">Buscando…</p>
                                )}
                                {g.itens.map(r => {
                                    const i = proximo();
                                    return linha({
                                        key: r.chave, i, icone: <Icone />, titulo: r.titulo,
                                        subtitulo: r.subtitulo, etiqueta: r.etiqueta, tom: r.tom,
                                        aoEscolher: () => linhasRef.current[i]?.escolher(),
                                    });
                                })}
                                {g.verTodos && g.total > g.itens.length && (() => {
                                    const i = proximo();
                                    return linha({
                                        key: `${g.id}-todos`, i, icone: <LuSearch />,
                                        titulo: <>Ver {g.total} em {g.rotulo.toLowerCase()} com “{procurado}”</>,
                                        aoEscolher: () => linhasRef.current[i]?.escolher(),
                                    });
                                })()}
                            </div>
                        );
                    })}

                    {telasAchadas.length > 0 && (
                        <div className="pal-grupo">
                            <div className="pal-grupo-tit">Ir para</div>
                            {telasAchadas.map((t: TelaBuscavel) => {
                                const Icone = t.icone;
                                const i = proximo();
                                return linha({
                                    key: t.chave, i, icone: <Icone />, titulo: t.titulo,
                                    subtitulo: t.secao,
                                    aoEscolher: () => linhasRef.current[i]?.escolher(),
                                });
                            })}
                        </div>
                    )}

                    {acoesAchadas.length > 0 && (
                        <div className="pal-grupo">
                            <div className="pal-grupo-tit">Ações</div>
                            {acoesAchadas.map(a => {
                                const Icone = a.efeito === 'tema'
                                    ? (theme === 'dark' ? ICONE_TEMA.escuro : ICONE_TEMA.claro)
                                    : a.icone;
                                const titulo = rotuloDaAcao(a);
                                const i = proximo();
                                return linha({
                                    key: a.chave, i, icone: <Icone />, titulo,
                                    subtitulo: a.descricao,
                                    aoEscolher: () => linhasRef.current[i]?.escolher(),
                                });
                            })}
                        </div>
                    )}
                </div>

                <div className="pal-rodape">
                    <span><kbd><LuArrowUp /></kbd><kbd><LuArrowDown /></kbd> para andar</span>
                    <span><kbd><LuCornerDownLeft /></kbd> para abrir</span>
                    <span><kbd>Esc</kbd> para fechar</span>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default BuscaGlobal;
